import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./repair&restore.css";

import woelbungsrekonstruktion from "./images/Wölbungsrekonstruktion.png";
import doublierung from "./images/Doublierung.jpg";
import wurmschaden from "./images/Wurmschaden.JPG";
import patches from "./images/patches.jpeg";
import modell3d from "./images/3D Modell.jpg";
import stegschneiden from "./images/stegschneiden.jpg";
import stimmeschneiden from "./images/stimmeschneiden.jpg";
import rissleimen from "./images/rissleimen.jpg";
import lackretusche from "./images/lackretusche.JPG";
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const RepairAndRestore = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeTab, setActiveTab] = useState("technology");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  i18n.addResourceBundle("en", "repairAndRestore", enTranslations, true, true);
  i18n.addResourceBundle("de", "repairAndRestore", deTranslations, true, true);

  useEffect(() => {
    const header = document.querySelector(".header");
    let headerHeight = 0;
    if (header) {
      headerHeight = header.offsetHeight;
      setHeaderHeight(headerHeight);
    }

    const handleScroll = () => {
      // Calculate when the original tab section is almost out of view
      const heroHeight = window.innerHeight * 0.7; // Hero section height
      const quoteHeight = 120; // Approximate quote section height
      const tabSectionHeight = 250; // Tab section height

      // Switch to fixed tab only when original tab is completely scrolled out of view
      const scrollThreshold = heroHeight + quoteHeight + tabSectionHeight + 200;
      const shouldBeScrolled = window.scrollY > scrollThreshold;

      // Add a small delay to prevent flickering during rapid scroll
      if (shouldBeScrolled !== isScrolled) {
        setTimeout(() => setIsScrolled(shouldBeScrolled), 50);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrolled]);

  const technologyExamples = [
    {
      title: t("repairAndRestore:tech_example_1_title"),
      description: t("repairAndRestore:tech_example_1_desc"),
      image: woelbungsrekonstruktion,
    },
    {
      title: t("repairAndRestore:tech_example_2_title"),
      description: t("repairAndRestore:tech_example_2_desc"),
      image: doublierung,
    },
    {
      title: t("repairAndRestore:tech_example_3_title"),
      description: t("repairAndRestore:tech_example_3_desc"),
      image: wurmschaden,
    },
    {
      title: t("repairAndRestore:tech_example_4_title"),
      description: t("repairAndRestore:tech_example_4_desc"),
      image: patches,
    },
    {
      title: t("repairAndRestore:tech_example_5_title"),
      description: t("repairAndRestore:tech_example_5_desc"),
      image: modell3d,
    },
  ];

  const traditionExamples = [
    {
      title: t("repairAndRestore:trad_example_1_title"),
      description: t("repairAndRestore:trad_example_1_desc"),
      image: stegschneiden,
    },
    {
      title: t("repairAndRestore:trad_example_2_title"),
      description: t("repairAndRestore:trad_example_2_desc"),
      image: stimmeschneiden,
    },
    {
      title: t("repairAndRestore:trad_example_3_title"),
      description: t("repairAndRestore:trad_example_3_desc"),
      image: rissleimen,
    },
    {
      title: t("repairAndRestore:trad_example_4_title"),
      description: t("repairAndRestore:trad_example_4_desc"),
      image: lackretusche,
    },
  ];

  const currentExamples =
    activeTab === "technology" ? technologyExamples : traditionExamples;

  const handleTabSwitch = (newTab) => {
    if (newTab === activeTab) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(newTab);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  };

  return (
    <main className="repair-restore-container" role="main">
      {/* Hero Section */}
      <section
        className="hero-section"
        role="banner"
        aria-labelledby="hero-heading"
      >
        <div className="hero-title-left">
          <h1
            id="hero-heading"
            className="focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={t(
              "repairAndRestore:hero_title_aria",
              "Seitentitel: {{title}}",
              {
                title: `${t("repairAndRestore:hero_repair")} ${t(
                  "repairAndRestore:hero_restore",
                )}`,
              },
            )}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("repairAndRestore:hero_repair")}
          </h1>
          <h1
            className="focus-highlight"
            tabIndex={0}
            role="text"
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("repairAndRestore:hero_and")}
          </h1>
        </div>
        <div className="hero-title-right">
          <h1
            className="focus-highlight"
            tabIndex={0}
            role="text"
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            {t("repairAndRestore:hero_restore")}
          </h1>
        </div>
      </section>

      {/* Quote Section */}
      <section
        className="quote-section"
        role="region"
        aria-labelledby="quote-heading"
      >
        <h2 id="quote-heading" className="sr-only">
          {t("repairAndRestore:quote_section_heading", "Unser Leitspruch")}
        </h2>
        <p
          className="quote-text focus-highlight"
          tabIndex={0}
          role="text"
          aria-label={t(
            "repairAndRestore:quote_text_aria",
            "Leitspruch: {{text}}",
            { text: t("repairAndRestore:quote_text") },
          )}
          style={{ pointerEvents: "none" }}
          onFocus={(e) => {
            e.target.style.pointerEvents = "auto";
          }}
          onBlur={(e) => {
            e.target.style.pointerEvents = "none";
          }}
        >
          {t("repairAndRestore:quote_text")}
        </p>
      </section>

      {/* Original Tab Section (shown when not scrolled) */}
      {!isScrolled && (
        <section
          className="tab-section"
          role="tablist"
          aria-label={t(
            "repairAndRestore:tab_navigation",
            "Reparatur-Kategorien",
          )}
        >
          <div className="tab-background"></div>
          <div className="tab-buttons">
            <button
              className={`tab-button focus-highlight ${
                activeTab === "technology" ? "active" : ""
              }`}
              onClick={() => handleTabSwitch("technology")}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === "technology"}
              aria-controls="examples-content"
              aria-label={t(
                "repairAndRestore:tab_technology_aria",
                "Technologie-basierte Reparaturen anzeigen",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabSwitch("technology");
                } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  handleTabSwitch(
                    activeTab === "technology" ? "tradition" : "technology",
                  );
                }
              }}
            >
              <h2>{t("repairAndRestore:tab_technology")}</h2>
            </button>
            <button
              className={`tab-button focus-highlight ${
                activeTab === "tradition" ? "active" : ""
              }`}
              onClick={() => handleTabSwitch("tradition")}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === "tradition"}
              aria-controls="examples-content"
              aria-label={t(
                "repairAndRestore:tab_tradition_aria",
                "Traditionelle Reparaturen anzeigen",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabSwitch("tradition");
                } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  handleTabSwitch(
                    activeTab === "technology" ? "tradition" : "technology",
                  );
                }
              }}
            >
              <h2>{t("repairAndRestore:tab_tradition")}</h2>
            </button>
          </div>
        </section>
      )}

      {/* Fixed Tab Section (shown when scrolled) */}
      {isScrolled && (
        <section
          className="fixed-tab-section"
          role="tablist"
          aria-label={t(
            "repairAndRestore:tab_navigation",
            "Reparatur-Kategorien",
          )}
        >
          <div className="tab-buttons">
            <button
              className={`tab-button focus-highlight ${
                activeTab === "technology" ? "active" : ""
              }`}
              onClick={() => handleTabSwitch("technology")}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === "technology"}
              aria-controls="examples-content"
              aria-label={t(
                "repairAndRestore:tab_technology_aria",
                "Technologie-basierte Reparaturen anzeigen",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabSwitch("technology");
                } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  handleTabSwitch(
                    activeTab === "technology" ? "tradition" : "technology",
                  );
                }
              }}
            >
              <h2>{t("repairAndRestore:tab_technology")}</h2>
            </button>
            <button
              className={`tab-button focus-highlight ${
                activeTab === "tradition" ? "active" : ""
              }`}
              onClick={() => handleTabSwitch("tradition")}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === "tradition"}
              aria-controls="examples-content"
              aria-label={t(
                "repairAndRestore:tab_tradition_aria",
                "Traditionelle Reparaturen anzeigen",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTabSwitch("tradition");
                } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  handleTabSwitch(
                    activeTab === "technology" ? "tradition" : "technology",
                  );
                }
              }}
            >
              <h2>{t("repairAndRestore:tab_tradition")}</h2>
            </button>
          </div>
        </section>
      )}

      {/* Spacer to maintain layout when original tab section is hidden */}
      {isScrolled && (
        <div className="tab-spacer" style={{ height: "250px" }}></div>
      )}

      {/* Examples Section */}
      <section
        id="examples-content"
        className={`examples-section ${isTransitioning ? "transitioning" : ""}`}
        role="tabpanel"
        aria-labelledby={
          activeTab === "technology" ? "tab-technology" : "tab-tradition"
        }
        aria-label={t(
          "repairAndRestore:examples_section",
          "Reparatur-Beispiele",
        )}
      >
        {currentExamples.map((example, index) => (
          <article
            key={index}
            className={`example-item ${
              index % 2 === 0 ? "left-image" : "right-image"
            }`}
            style={{
              transitionDelay: isTransitioning ? "0ms" : `${index * 100}ms`,
            }}
            role="article"
            aria-labelledby={`example-title-${index}`}
          >
            <div
              className="example-image"
              role="img"
              aria-describedby={`example-description-${index}`}
            >
              <ResponsiveImage sizes="(max-width: 768px) 100vw, 50vw"
                src={example.image}
                alt={t(
                  "repairAndRestore:example_image_alt",
                  "{{category}} Reparatur: {{title}} - Detailansicht der handwerklichen Arbeit bei Geigenbau Jacobi",
                  {
                    category:
                      activeTab === "technology"
                        ? "Technologische"
                        : "Traditionelle",
                    title: example.title,
                  },
                )}
              />
            </div>
            <div className="example-content">
              <h3
                id={`example-title-${index}`}
                className="focus-highlight"
                tabIndex={0}
                role="text"
                aria-label={t(
                  "repairAndRestore:example_title_aria",
                  "Reparatur-Titel: {{title}}",
                  { title: example.title },
                )}
                style={{ pointerEvents: "none" }}
                onFocus={(e) => {
                  e.target.style.pointerEvents = "auto";
                }}
                onBlur={(e) => {
                  e.target.style.pointerEvents = "none";
                }}
              >
                {example.title}
              </h3>
              <p
                id={`example-description-${index}`}
                className="focus-highlight"
                tabIndex={0}
                role="text"
                aria-label={t(
                  "repairAndRestore:example_desc_aria",
                  "Beschreibung: {{description}}",
                  { description: example.description },
                )}
                style={{ pointerEvents: "none" }}
                onFocus={(e) => {
                  e.target.style.pointerEvents = "auto";
                }}
                onBlur={(e) => {
                  e.target.style.pointerEvents = "none";
                }}
              >
                {example.description}
              </p>
            </div>
          </article>
        ))}
      </section>

      {/* Contact CTA Section */}
      <section
        className="contact-cta-section focus-highlight"
        onClick={() =>
          navigate(i18n.language === "en" ? "/en/contact" : "/contact")
        }
        role="button"
        tabIndex={0}
        aria-label={t(
          "repairAndRestore:contact_cta_aria",
          "Zur Kontaktseite für Reparatur-Beratung navigieren",
        )}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(i18n.language === "en" ? "/en/contact" : "/contact");
          }
        }}
      >
        <div className="contact-cta-content">
          <h2>{t("repairAndRestore:contact_cta")}</h2>
        </div>
      </section>
    </main>
  );
};

export default RepairAndRestore;
